import { useCallback, useState } from "react";
import { CalendarX2, MapPin } from "lucide-react";
import { meApi } from "../../api/resources";
import type { MyAssignment } from "../../api/types";
import { PageHeader } from "../../components/ComingSoon";
import { Tabs } from "../../components/Overlay";
import { LoadState, useLoad } from "../admin/shared";
import { ShiftStatusPill, fmtWorkDate, timeRange } from "../admin/shifts/shiftUi";

function Card({ a }: { a: MyAssignment }) {
  const cancelled = a.status === "CANCELLED";
  return (
    <article data-testid={`my-assignment-${a.id}`} className={`grid gap-4 rounded-sm border border-white/10 bg-surface p-6 sm:grid-cols-[140px_1fr] ${cancelled ? "opacity-60" : ""}`}>
      <div>
        <p className="label-caps text-signal">{fmtWorkDate(a.work_date, { weekday: "long" })}</p>
        <p data-testid={`my-assignment-date-${a.id}`} className={`font-display text-3xl font-bold uppercase leading-none text-white ${cancelled ? "line-through" : ""}`}>{fmtWorkDate(a.work_date, { month: "short", day: "numeric" })}</p>
        <p className="mt-1 text-xs text-zinc-500">{fmtWorkDate(a.work_date, { year: "numeric" })}</p>
      </div>
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 data-testid={`my-assignment-venue-${a.id}`} className="text-lg font-semibold text-white">{a.venue_name}</h3>
          <ShiftStatusPill status={a.status} testId={`my-assignment-status-${a.id}`} />
        </div>
        <p data-testid={`my-assignment-time-${a.id}`} className="text-sm text-zinc-200">{timeRange(a)}</p>
        {a.venue_address && <p className="flex items-center gap-1.5 text-xs text-zinc-400"><MapPin className="h-3.5 w-3.5" />{a.venue_address}</p>}
        {a.role && <p data-testid={`my-assignment-role-${a.id}`} className="text-xs text-zinc-400">Role: <span className="text-zinc-200">{a.role}</span></p>}
        {a.instructions && <p data-testid={`my-assignment-instructions-${a.id}`} className="whitespace-pre-wrap rounded-sm border border-white/10 bg-ink p-3 text-sm text-zinc-300">{a.instructions}</p>}
      </div>
    </article>
  );
}

export default function MyAssignments() {
  const [scope, setScope] = useState<"upcoming" | "past">("upcoming");
  const loader = useCallback(() => meApi.assignments(scope), [scope]);
  const { data, error } = useLoad<MyAssignment[]>(loader);
  return (
    <section data-testid="my-assignments-page" className="animate-rise">
      <PageHeader title="Assignments" />
      <Tabs active={scope} onChange={(k) => setScope(k as "upcoming" | "past")} tabs={[{ key: "upcoming", label: "Upcoming" }, { key: "past", label: "Past" }]} />
      <LoadState error={error} loading={!data && !error} testId="my-assignments-error" />
      {data && data.length === 0 && (
        <div data-testid="my-assignments-empty" className="flex flex-col items-center rounded-sm border border-dashed border-white/15 px-6 py-16 text-center">
          <CalendarX2 className="h-9 w-9 text-zinc-500" />
          <p className="mt-4 font-display text-xl font-bold uppercase text-white">{scope === "upcoming" ? "Nothing scheduled yet" : "No past assignments"}</p>
          <p className="mt-2 max-w-sm text-sm text-zinc-400">{scope === "upcoming" ? "When the office assigns you to an event it will show up here." : "Shifts you've worked will be listed here."}</p>
        </div>
      )}
      {data && data.length > 0 && <div className="space-y-4">{data.map((a) => <Card key={a.id} a={a} />)}</div>}
      <p className="mt-8 text-xs text-zinc-500">Times are shown in each venue's local time. Questions about a shift? Contact the office.</p>
    </section>
  );
}
